import { Layers, SlidersHorizontal, Music, Tv, Zap, CheckCircle, ArrowLeft, PlayCircle, Star } from 'lucide-react';
import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';

// This is the final, production-ready FeaturesPage.
// It includes a new hero section for the tutorial video and a sample scroll.

const FeaturesPage = ({ onNavigate, setPurchaseIntent }) => {
   const [isVideoPlaying, setIsVideoPlaying] = useState(false); 
  const features = [
    {
      title: 'The Command Bar: Your Secret Weapon',
      description: 'The floating Command Bar is designed for maximum speed. Set global typography, sequence order, and timing with just a few clicks, getting you 90% of the way to a finished product in seconds.',
      points: [
        'Instantly change font for the entire project.',
        'Set scroll speed or a target duration.',
        'Define the sequence of cards and scrolls.',
      ],
      image: 'https://placehold.co/1200x800/18181b/9333ea?text=Command+Bar+UI',
    },
    {
      title: 'The Inspector: Pixel-Perfect Control',
      description: 'When details matter, the Inspector Panel gives you granular control over every single element. Select any credit to fine-tune its properties without affecting anything else.',
      points: [
        'Override global styles for individual credits.',
        'Adjust advanced typography like line height and letter spacing.',
        'Configure complex logo grids and title card layouts.',
      ],
      image: 'https://placehold.co/1200x800/18181b/34d399?text=Inspector+Panel+UI',
    },
    {
      title: 'Multi-Layer Title Cards',
      description: 'Create stunning, animated title cards with a powerful layering system. Each card can have multiple text and logo layers, each with its own unique font, size, color, and style.',
      points: [
        'Add unlimited text layers to any card.',
        'Animate the entire card with professional fades and slides.',
        'Perfect for main titles, section headers, and producer cards.',
      ],
      image: 'https://placehold.co/1200x800/18181b/f59e0b?text=Title+Card+Editor',
    },
    {
      title: 'Music Sync & Professional Exports',
      description: 'Eliminate guesswork and integrate seamlessly with your post-production workflow. Sync to your score and export in formats the pros use.',
      points: [
        'Upload your score to perfectly sync your scroll duration.',
        'Export to Apple ProRes for the highest quality.',
        'Generate NLE data files for Resolve, Premiere, and FCPX.',
      ],
      image: 'https://placehold.co/1200x800/18181b/3b82f6?text=Export+Modal+UI',
    },
  ];

  {/*
  const testimonials = [
    { quote: "This saved me hours. What used to be a tedious final step is now quick and genuinely enjoyable.", name: "Alex Chen", title: "Indie Filmmaker" },
    { quote: "The Command Bar is genius. I can set up the entire look in a minute, and the scroll quality is flawless.", name: "Maria Garcia", title: "Post-Production Supervisor" },
    { quote: "Finally, a credits tool that understands a professional workflow. The ProRes and NLE exports are essential.", name: "David Kim", title: "Lead Editor" },
  ];
  */}
  
  const sampleCredits = [
      { role: 'Directed By', name: 'AVA GREEN' },
      { role: 'Written By', name: 'ELIJAH CARTER' },
      { role: 'Produced By', name: 'SOFIA NADEEM' },
      { type: 'spacer' },
      { role: 'Director of Photography', name: 'LEO YAMAMOTO' },
      { role: 'Production Designer', name: 'CHLOE MONROE' },
      { role: 'Edited By', name: 'ISAAC BAUTISTA' },
      { type: 'spacer' },
      { role: 'Music By', name: 'NOAH ROSSI' },
  ];

  // Handle the CTA to go to signup with demo plan pre-selected
  const handleGetStarted = () => {
    setPurchaseIntent('demo');
    onNavigate('signup');
  };

  return (
    <div className="bg-black text-gray-200 font-poppins antialiased">
        <style>{`
          .neon-purple-text {
            text-shadow: 0 0 8px rgba(192, 132, 252, 0.6);
          }
          .video-placeholder:hover .play-icon {
            transform: scale(1.1);
            background-color: rgba(192, 132, 252, 0.9);
          }
          .scroll-container {
            mask-image: linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%);
            -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%);
          }
          .scroll-content {
            animation: buttery-scroll 30s linear infinite;
          }
          @keyframes buttery-scroll {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(-100%);
            }
          }
        `}</style>

        {/* Header Navigation */}
        <header className="absolute top-0 left-0 w-full p-6 z-10 flex justify-between items-center">
            <button onClick={() => onNavigate('landing')} className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors">
                <ArrowLeft size={18} />
                Back to Home
            </button>
            {/* --- NEW BRANDING BLOCK --- */}
            <div className="text-right">
              <div className="text-2xl font-thin tracking-wide text-purple-300">
                BLACKBOX<span className="text-yellow-400">cr</span>
              </div>
              <p className="text-[10px] text-zinc-500 tracking-widest -mt-1.5">
                <span className="font-bold text-zinc-300 tracking-wider">closing credits</span>
              </p>
            </div>
            {/* --- END BRANDING BLOCK --- */}
        </header>

        {/* Video Hero Section - Fixed */}
<section className="relative text-center py-24 px-6 bg-zinc-900/50 overflow-hidden">
    <div className="absolute inset-0 bg-grid-zinc-800/20 [mask-image:linear-gradient(to_bottom,white_5%,transparent_90%)]"></div>
    <div className="relative z-10 max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            From Zero to Preview in <span className="text-purple-400 neon-purple-text">3 Minutes</span>
        </h1>
        <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
            With our WYSIYG preview panel, you can preview your credits faster than ever.
        </p>
        
        <div 
    onClick={() => setIsVideoPlaying(true)}
    className="video-placeholder aspect-video max-w-3xl mx-auto bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl flex items-center justify-center cursor-pointer overflow-hidden group relative"
    style={{
        backgroundImage: `url('https://vumbnail.com/1128406282.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    }}
>
    {/* Dark overlay for better play button visibility */}
    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-30 transition-all duration-300"></div>
    
    <div className="play-icon bg-purple-600/80 text-white p-6 rounded-full transition-all duration-300 shadow-lg relative z-10">
        <PlayCircle size={48} />
    </div>
</div>
    </div>

    {/* Video Modal */}
    {isVideoPlaying && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl w-full">
                <button
                    onClick={() => setIsVideoPlaying(false)}
                    className="absolute -top-12 right-0 text-white hover:text-gray-300 text-xl z-10"
                >
                    ✕ Close
                </button>

                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <iframe
                        className="w-full h-full"
                        src="https://player.vimeo.com/video/1128406282?badge=0&autopause=0&player_id=0&app_id=58479"
                        title="BLACKBOXcr Tutorial Video"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                        allowFullScreen
                    ></iframe>
                </div>
            </div>
        </div>
    )}
</section>

        {/* Features Sections */}
        {features.map((feature, index) => (
            <section key={index} className={`py-20 px-6 ${index % 2 === 1 ? 'bg-zinc-900/50' : 'bg-black'}`}>
                <div className={`container mx-auto grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:grid-flow-col-dense' : ''}`}>
                    <div className={`${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                        <h2 className="text-3xl font-bold text-white mb-4">{feature.title}</h2>
                        <p className="text-zinc-400 mb-6">{feature.description}</p>
                        <ul className="space-y-3">
                            {feature.points.map((point, i) => (
                                <li key={i} className="flex items-start">
                                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={`${index % 2 === 1 ? 'md:col-start-1' : ''}`}>
                        <img src={feature.image} alt={feature.title} className="rounded-xl shadow-2xl border border-zinc-800" />
                    </div>
                </div>
            </section>
        ))}

        {/* Buttery Scroll Section */}
        <section className="py-20 px-6 text-center bg-black">
            <div className="container mx-auto max-w-4xl">
                <h2 className="text-3xl font-bold text-white mb-4">Experience the Cinematic Difference</h2>
                <p className="text-zinc-400 mb-8">WYSIYG Previews. This isn't just a scroll. It's a judder-free, perfectly smooth render powered by our proprietary motion engine.</p>
                
                <div className="scroll-container relative aspect-[9/16] max-h-[70vh] w-full max-w-sm mx-auto bg-black rounded-2xl border-2 border-zinc-800 overflow-hidden shadow-2xl">
                    <div className="scroll-content absolute w-full top-0">
                        {/* Add some top padding to start off-screen */}
                        <div className="pt-24">
                            {sampleCredits.map((credit, index) => (
                                <div key={index} className={`text-center text-2xl mb-8 ${credit.type === 'spacer' ? 'h-16' : ''}`}>
                                    {credit.role && <p className="text-zinc-400 uppercase tracking-widest text-sm">{credit.role}</p>}
                                    {credit.name && <p className="text-white font-light tracking-wider">{credit.name}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Testimonials Section 
        <section className="py-20 px-6 bg-zinc-900/50">
            <div className="container mx-auto">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-white">Why Professionals Choose BLACKBOXcr</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {testimonials.map((testimonial, index) => (
                        <div key={index} className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800">
                            <div className="flex text-yellow-400 mb-4">{[...Array(5)].map((_, i) => <Star key={i} />)}</div>
                            <p className="text-lg italic text-zinc-300 mb-6">"{testimonial.quote}"</p>
                            <div>
                                <p className="font-semibold text-white">{testimonial.name}</p>
                                <p className="text-zinc-400">{testimonial.title}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
        */}

        {/* Final CTA */}
        <section className="py-20 text-center px-6">
            <div className="container mx-auto">
                <h2 className="text-3xl font-bold text-white mb-4">Ready to Create Your Credits?</h2>
                <p className="text-zinc-400 mb-8">Start your free 7-day demo and experience the speed for yourself.</p>
                <button onClick={handleGetStarted} className="bg-green-500 text-black font-semibold px-8 py-4 rounded-lg hover:bg-green-400 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-green-500/30">
                    Get Started Now
                </button>
            </div>
        </section>
    </div>
  );
};

export default FeaturesPage;